import React, { useCallback, useEffect, useState } from 'react';
import { leadsService } from '../services/api';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { campo, acaoPrincipal, acaoSecundaria, mensagemDoErroDeCarga } from '../design';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import { EtapaEditavel, EtapasDoFunil, etapasDaResposta, payloadDasEtapas, recusaDasEtapas } from '../utils/crm';

/**
 * Configurações → Etapas do funil (12/10/2026, A4 do bloco A, contrato da #409 do backend). Nome, ordem, ativa e a
 * marca "etapa da assinatura" (onde o aceite da proposta deixa o lead e onde a ficha oferece gerar a assinatura).
 *
 * - Backend sem a #409 (404): diz que as etapas ainda são fixas e não oferece editar.
 * - O PUT recusa em bloco; a tela mostra a frase do servidor e uma linha por problema ou impedimento (etapa com lead
 *   dentro, ou com congelado que volta para ela, não se desativa nem se renomeia).
 */
type Estado = { fase: 'lendo' } | { fase: 'sem-409' } | { fase: 'erro'; motivo: string } | { fase: 'pronto' };

export default function EtapasDoFunilSettings({ onSalvo }: { onSalvo?: (funil: EtapasDoFunil) => void }) {
  const confirmar = useConfirm();
  const avisar = useAviso();
  const [estado, setEstado] = useState<Estado>({ fase: 'lendo' });
  const [lista, setLista] = useState<EtapaEditavel[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [recusa, setRecusa] = useState<{ frase: string; linhas: string[] } | null>(null);

  const ler = useCallback(async () => {
    setEstado({ fase: 'lendo' });
    try {
      const r = await leadsService.getFunnelStages();
      const etapas = (Array.isArray(r?.etapas) ? r.etapas : [])
        .slice()
        .sort((a: any, b: any) => (a.ordem ?? 0) - (b.ordem ?? 0))
        .map((e: any) => ({ id: e.id, nome: e.nome, ativa: !!e.ativa, etapaDaAssinatura: !!e.etapaDaAssinatura }));
      setLista(etapas);
      setEstado({ fase: 'pronto' });
      return r;
    } catch (e: any) {
      setEstado(e?.response?.status === 404 ? { fase: 'sem-409' } : { fase: 'erro', motivo: mensagemDoErroDeCarga(e) });
      return null;
    }
  }, []);
  useEffect(() => { ler(); }, [ler]);

  const mudar = (i: number, campoMudado: Partial<EtapaEditavel>) => {
    setRecusa(null);
    setLista((l) => l.map((e, j) => {
      if (campoMudado.etapaDaAssinatura) return { ...e, etapaDaAssinatura: j === i };
      return j === i ? { ...e, ...campoMudado } : e;
    }));
  };
  const mover = (i: number, delta: number) => {
    setRecusa(null);
    setLista((l) => {
      const j = i + delta;
      if (j < 0 || j >= l.length) return l;
      const n = [...l];
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
  };

  const salvar = async () => {
    if (!(await confirmar({
      title: 'Gravar as etapas do funil?',
      message: 'Muda as colunas do board e a trilha da ficha para todo mundo.',
      consequence: 'Etapa com lead dentro (ou com congelado que volta para ela) não se desativa nem se renomeia: o servidor recusa e diz quais.',
      confirmLabel: 'Gravar etapas',
    }))) return;
    setSalvando(true);
    setRecusa(null);
    try {
      const res = await leadsService.putFunnelStages(payloadDasEtapas(lista));
      const mudancas: string[] = Array.isArray(res?.mudancas) ? res.mudancas : [];
      avisar(mudancas.length ? `Etapas gravadas: ${mudancas.join('; ')}.` : 'Etapas gravadas, sem mudança.', 'success');
      const relida = await ler();
      if (relida && onSalvo) onSalvo(etapasDaResposta(relida));
    } catch (e: any) {
      setRecusa(recusaDasEtapas(e));
    }
    setSalvando(false);
  };

  return (
    <div className='bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden' data-etapas-do-funil>
      <div className='p-4 border-b border-slate-800'>
        <h3 className='text-white font-semibold'>Etapas do funil</h3>
        <p className='text-slate-400 text-sm'>As colunas do board do CRM, na ordem. A etapa da assinatura é onde a ficha oferece gerar a assinatura.</p>
      </div>
      <div className='p-6 space-y-3'>
        {estado.fase === 'lendo' && <p className='text-sm text-slate-400'>Lendo as etapas…</p>}
        {estado.fase === 'sem-409' && (
          <p className='text-sm text-slate-400' data-etapas-fixas>
            O servidor ainda não tem etapas configuráveis: hoje as seis etapas são fixas (Novo, Contato, POC, Proposta, Negociação, Geração Assinatura).
          </p>
        )}
        {estado.fase === 'erro' && (
          <div role='alert' className={`px-3 py-2 rounded border text-sm ${TONE_SURFACE.danger} ${TONE_BORDER.danger} ${TONE_TEXT.danger}`}>
            Não consegui ler as etapas do funil: {estado.motivo}.{' '}
            <button type='button' onClick={ler} className='underline'>Tentar de novo</button>
          </div>
        )}
        {estado.fase === 'pronto' && (
          <>
            <ol className='space-y-2'>
              {lista.map((e, i) => (
                <li key={e.id ?? `nova-${i}`} className='flex items-center gap-2' data-etapa={e.nome}>
                  <span className='w-6 text-right text-xs text-slate-500 tabular-nums'>{i + 1}</span>
                  <input
                    type='text'
                    value={e.nome}
                    maxLength={50}
                    onChange={(ev) => mudar(i, { nome: ev.target.value })}
                    aria-label={`Nome da etapa ${i + 1}`}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm ${campo}`}
                  />
                  <button type='button' onClick={() => mover(i, -1)} disabled={i === 0} title='Subir' className={`px-2 py-1 rounded text-sm disabled:opacity-30 ${acaoSecundaria}`}>↑</button>
                  <button type='button' onClick={() => mover(i, 1)} disabled={i === lista.length - 1} title='Descer' className={`px-2 py-1 rounded text-sm disabled:opacity-30 ${acaoSecundaria}`}>↓</button>
                  <label className='flex items-center gap-1 text-xs text-slate-300 whitespace-nowrap'>
                    <input type='checkbox' checked={e.ativa} onChange={(ev) => mudar(i, { ativa: ev.target.checked })} /> ativa
                  </label>
                  <label className='flex items-center gap-1 text-xs text-slate-300 whitespace-nowrap'>
                    <input type='radio' name='etapa-da-assinatura' checked={e.etapaDaAssinatura} onChange={() => mudar(i, { etapaDaAssinatura: true })} /> assinatura
                  </label>
                </li>
              ))}
            </ol>
            <button type='button' onClick={() => { setRecusa(null); setLista((l) => [...l, { nome: '', ativa: true, etapaDaAssinatura: false }]); }} className={`px-3 py-1.5 rounded-lg text-sm ${acaoSecundaria}`}>
              + Nova etapa
            </button>
            {recusa && (
              <div role='alert' className={`px-3 py-2 rounded border text-sm ${TONE_SURFACE.warning} ${TONE_BORDER.warning} ${TONE_TEXT.warning}`} data-recusa-das-etapas>
                <p>Não gravei: {recusa.frase}</p>
                {recusa.linhas.length > 0 && <ul className='mt-1 list-disc pl-5'>{recusa.linhas.map((l, i) => <li key={i}>{l}</li>)}</ul>}
              </div>
            )}
            <div className='flex justify-end'>
              <button type='button' onClick={salvar} disabled={salvando} className={`px-4 py-2 rounded-lg text-sm disabled:opacity-50 ${acaoPrincipal}`}>
                {salvando ? 'Gravando…' : 'Gravar etapas'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
